/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DirectionalHint, ITextField } from '@fluentui/react';
import { ActionButton, IconButton } from '@fluentui/react/lib/Button';
import { FontIcon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { Stack } from '@fluentui/react/lib/Stack';
import { classNamesFunction, getId } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { DragAndDropList } from 'src/core/components/DragAndDropList';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { logButtonClick, logButtonClickWithCustomData } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { noop } from 'src/core/utils/base';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { dequeueTeachingBubble, queueTeachingBubble } from 'src/luis/projects/events/utils/teachingBubbles';
import { useQnaMetricActions } from 'src/qnaMaker/core/hooks/useQnaMetricActions';
import { QnaMakerQnaActiveLearningSuggestion } from 'src/qnaMaker/core/services/qnas/QnaMakerAssetsSwaggers';
import { QnaMakerLanguageDirection } from 'src/qnaMaker/core/types/QnaMakerLanguages';
import { qnaMakerTeachingBubbleMessages } from 'src/qnaMaker/core/utils/qnaMakerTeachingBubbleMessages';

type QuestionObj = { index: number; question: string };

type EditModeDisplayProps = {
    required?: boolean;
    autoFocus?: boolean;
    onDismiss?: () => void;
    questionObj?: QuestionObj;
    onBlur?: (time: number) => void;
    onDelete?: (q: string) => void;
    dir: QnaMakerLanguageDirection;
    isComponentInEditMode?: boolean;
    addAlternatePhraseButtonId: string;
    onSubmit: (questionObj: QuestionObj) => void;
};

export type QnaMakerQuestionsColumnRendererProps = {
    pairId: number;
    questions: string[];
    isInEditMode?: boolean;
    isInAddMode?: boolean;
    onEditQuestions: (questions: string[], activeLearningSuggestions?: QnaMakerQnaActiveLearningSuggestion[]) => void;
    isAddActionDisabled: boolean;
    firstPopulatedQna?: boolean;
};

const messages = defineMessages({
    textfieldPlaceholder: {
        id: 'qnaMaker.qnaMakerQuestionsColumnRenderer.textfieldPlaceholder',
        defaultMessage: 'Add question and press enter'
    },
    addAlternatePhrase: {
        id: 'qnaMaker.qnaMakerQuestionsColumnRenderer.addAlternatePhrase',
        defaultMessage: 'Add alternate phrase'
    },
    deleteQuestion: {
        id: 'qnaMaker.qnaMakerQuestionsColumnRenderer.deleteQuestion',
        defaultMessage: 'Delete question'
    },
    submitChanges: {
        id: 'qnaMaker.qnaMakerQuestionsColumnRenderer.submitChanges',
        defaultMessage: 'Submit changes'
    },
    dismissChanges: {
        id: 'qnaMaker.qnaMakerQuestionsColumnRenderer.dismissChanges',
        defaultMessage: 'Dismiss changes'
    },
    acceptSuggestion: {
        id: 'qnaMaker.qnaMakerQuestionsColumnRenderer.acceptSuggestion',
        defaultMessage: 'Accept suggestion'
    },
    rejectSuggestion: {
        id: 'qnaMaker.qnaMakerQuestionsColumnRenderer.rejectSuggestion',
        defaultMessage: 'Reject suggestion'
    }
});

const localTheme = getScopedTheme('QnaMakerQuestionsColumnRenderer');

const submitIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { fontSize: '20px', lineHeight: '16px' } });
const dismissIconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({ root: { fontSize: '12px', lineHeight: '16px' } });

/**
 * A component that renders the question in edit mode.
 */
const EditModeDisplay = (props: EditModeDisplayProps) => {
    const {
        dir,
        questionObj,
        onBlur = noop,
        onSubmit,
        required = false,
        onDismiss = noop,
        autoFocus = false,
        onDelete = noop,
        addAlternatePhraseButtonId
    } = props;

    const { localize } = useLocalization();
    const isAddingNewQ = !questionObj;

    const [stateQuestionObj, setStateQuestionObj] = useUpdateState<QuestionObj>(questionObj || { index: -1, question: '' });

    const isModified = stateQuestionObj.question !== questionObj?.question;

    const handleKeyDown = (event: React.KeyboardEvent) => {
        switch (event.key) {
            case LuisKeyCodes.Enter:
                event.preventDefault();
                onSubmit(stateQuestionObj);
                document.getElementById(addAlternatePhraseButtonId)?.focus();
                break;
            case LuisKeyCodes.Escape:
                event.preventDefault();
                onDismiss();
                if (isModified) {
                    setStateQuestionObj(questionObj);
                }
                document.getElementById(addAlternatePhraseButtonId)?.focus();
                break;
            default:
        }
    };

    const textFieldIdRef = React.useRef<ITextField>(null);

    React.useEffect(() => {
        if (autoFocus && textFieldIdRef.current) {
            textFieldIdRef.current.focus();
        }
    }, [textFieldIdRef, autoFocus]);

    return (
        <Stack horizontal>
            <Stack.Item grow>
                <LuisTextField
                    data-automation-id="question"
                    dir={dir}
                    multiline
                    autoAdjustHeight
                    resizable={false}
                    required={required}
                    onKeyDown={handleKeyDown}
                    onTimeTakenChange={onBlur}
                    componentRef={textFieldIdRef}
                    value={stateQuestionObj.question}
                    placeholder={localize(messages.textfieldPlaceholder)}
                    styles={{ field: { resize: 'vertical !important' } }}
                    onChange={(_, newValue) => setStateQuestionObj({ question: newValue })}
                />
            </Stack.Item>

            {!isModified && (
                <Stack>
                    <LuisTooltip content={localize(messages.deleteQuestion)}>
                        <IconButton
                            aria-label={localize(messages.deleteQuestion)}
                            iconProps={{ iconName: 'Delete' }}
                            onClick={() => onDelete(questionObj.question)}
                        />
                    </LuisTooltip>
                </Stack>
            )}

            {(isAddingNewQ || isModified) && (
                <Stack horizontal verticalAlign="center">
                    <LuisTooltip content={localize(messages.submitChanges)}>
                        <IconButton
                            aria-label={localize(messages.submitChanges)}
                            iconProps={{ iconName: 'CheckMark', styles: submitIconStyles }}
                            onClick={() => onSubmit(stateQuestionObj)}
                        />
                    </LuisTooltip>

                    <LuisTooltip content={localize(messages.dismissChanges)}>
                        <IconButton
                            aria-label={localize(messages.dismissChanges)}
                            iconProps={{ iconName: 'ChromeClose', styles: dismissIconStyles }}
                            onClick={() => {
                                onDismiss();
                                if (isModified) {
                                    setStateQuestionObj(questionObj);
                                }
                            }}
                        />
                    </LuisTooltip>
                </Stack>
            )}
        </Stack>
    );
};

/**
 * A component rendering the questions column in the edit page QnAs.
 */
export const QnaMakerQuestionsColumnRenderer = observer((props: QnaMakerQuestionsColumnRendererProps) => {
    const {
        pairId,
        questions,
        onEditQuestions,
        isInEditMode = false,
        isInAddMode = false,
        isAddActionDisabled,
        firstPopulatedQna = false
    } = props;
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { rootStore, uiStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);
    const { languageDirection, id: currentProjectId } = rootStore.currentQnaMakerProjectStore;
    const {
        resetRowMetrics,
        setTimeTakenToEditQuestions,
        increaseDeletedQuestionsForPair,
        increaseEditedQuestionsForPair,
        increaseAddedQuestionsForPair,
        increaseQuestionClicksForPair,
        increaseTotalNumberOfClicksBeforeSave,
        setTimeTakenToAddOrDismissAlternateQuestionOutsideEditMode,
        getTimeTakenToAddOrDismissAlternateQuestionOutsideEditMode
    } = useQnaMetricActions();

    const addAlternatePhraseButtonId = React.useRef(getId());
    const firstAlternateQuestionId = React.useRef(getId());
    const [isAddingNewQuestion, setIsAddingNewQuestion] = useState<boolean>(isInAddMode);

    const mappedQuestions: QuestionObj[] = questions.map((question, index) => ({ question, index }));

    const onUpdateQuestion = (questionObj: QuestionObj) => {
        increaseQuestionClicksForPair(pairId);
        increaseEditedQuestionsForPair(pairId);
        if (questionObj.question) {
            onEditQuestions([...questions.slice(0, questionObj.index), questionObj.question, ...questions.slice(questionObj.index + 1)]);
        }
    };

    const onDeleteQuestion = (question: string) => {
        increaseQuestionClicksForPair(pairId);
        increaseDeletedQuestionsForPair(pairId);
        onEditQuestions(questions.filter(q => q !== question));
    };

    const onDismissQuestion = () => {
        if (isInEditMode || isInAddMode) {
            increaseQuestionClicksForPair(pairId);
        }
    };

    const onDismissForNewQuestion = () => {
        if (isInEditMode || isInAddMode) {
            increaseQuestionClicksForPair(pairId);
        } else {
            increaseTotalNumberOfClicksBeforeSave();

            logButtonClickWithCustomData(serviceName, 'EditKb', 'CancelAlternateQuestionsOutsideEditMode', {
                pivotName: 'Qnas',
                projectId: currentProjectId,
                timeTakenToCancelAlternateQuestion:
                    LuisConstants.QnaMaker.Metrics.addFieldBaseTimeInMilliseconds +
                    getTimeTakenToAddOrDismissAlternateQuestionOutsideEditMode(pairId),
                numberOfClicksToCancelAlternateQuestion: LuisConstants.QnaMaker.Metrics.addOrDismissAlternateQuestionClickCounter
            });

            resetRowMetrics(pairId);
        }

        setIsAddingNewQuestion(false);
    };

    const onAddQuestion = (questionObj: QuestionObj) => {
        if (isInEditMode || isInAddMode) {
            increaseQuestionClicksForPair(pairId);

            if (isAddingNewQuestion) {
                increaseAddedQuestionsForPair(pairId);
            }
        } else {
            increaseTotalNumberOfClicksBeforeSave();

            logButtonClickWithCustomData(serviceName, 'EditKb', 'AddAlternateQuestionsOutsideEditMode', {
                pivotName: 'Qnas',
                projectId: currentProjectId,
                timeTakenToAddAlternateQuestion:
                    LuisConstants.QnaMaker.Metrics.addFieldBaseTimeInMilliseconds +
                    getTimeTakenToAddOrDismissAlternateQuestionOutsideEditMode(pairId),
                numberOfClicksToAddAlternateQuestion: LuisConstants.QnaMaker.Metrics.addOrDismissAlternateQuestionClickCounter
            });

            resetRowMetrics(pairId);
        }

        if (questionObj.question) {
            onEditQuestions([...questions, questionObj.question]);
        }

        setIsAddingNewQuestion(false);
    };

    const onDragAndDropItems = (updatedQuestions: QuestionObj[]) => {
        if (!uiStore.teachingBubbles.QnaAlternateQuestions) {
            dequeueTeachingBubble('QnaAlternateQuestions');
        }

        onEditQuestions(updatedQuestions.map(questionObj => questionObj.question));
    };

    const handleOnTakenTimeChange = (time: number) => {
        if (isInEditMode || isInAddMode) {
            setTimeTakenToEditQuestions(pairId, time);
        } else {
            setTimeTakenToAddOrDismissAlternateQuestionOutsideEditMode(pairId, time);
        }
    };

    const onRenderQuestion = (item: QuestionObj): JSX.Element => (
        <Stack key={item.question} horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} data-selection-disabled>
            <>
                <RBACTooltip
                    hidden={canWrite}
                    displayedUserRole={displayedUserRole}
                    data-automation-id="dragButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <FontIcon
                            iconName="DragObject"
                            style={{ cursor: canWrite ? 'move' : '', fontSize: '16px' }}
                            id={firstPopulatedQna && item.index === 0 ? firstAlternateQuestionId.current : getId()}
                        />
                    }
                />

                <TruncatedText
                    maxWidth={'90%'}
                    dir={languageDirection}
                    styles={{ root: { color: localTheme.textColor } }}
                    title={item.question}>
                    {item.question}
                </TruncatedText>
            </>
        </Stack>
    );

    const onRenderQuestionInEditMode = (item: QuestionObj): JSX.Element => (
        <Stack key={item.question} verticalAlign="center" data-selection-disabled>
            <EditModeDisplay
                questionObj={item}
                dir={languageDirection}
                onSubmit={onUpdateQuestion}
                onDelete={onDeleteQuestion}
                autoFocus={item.index === 0}
                onDismiss={onDismissQuestion}
                onBlur={handleOnTakenTimeChange}
                required={!mappedQuestions.length || item.index === 0}
                addAlternatePhraseButtonId={addAlternatePhraseButtonId.current}
            />
        </Stack>
    );

    React.useEffect(() => {
        if (firstPopulatedQna && !uiStore.teachingBubbles.QnaAlternateQuestions) {
            queueTeachingBubble({
                key: 'QnaAlternateQuestions',
                message: { title: localize(qnaMakerTeachingBubbleMessages.alternateQuestionTitle), body: '' },
                hasGotIt: true,
                config: { target: `#${firstAlternateQuestionId.current}`, calloutProps: { directionalHint: DirectionalHint.leftCenter } }
            });

            return () => dequeueTeachingBubble('QnaAlternateQuestions');
        }
    }, [firstPopulatedQna, uiStore.teachingBubbles.QnaAlternateQuestions]);

    const handleAddAlternatePhrase = () => {
        if (isInEditMode || isInAddMode) {
            increaseQuestionClicksForPair(pairId);
        } else {
            increaseTotalNumberOfClicksBeforeSave();
        }

        logButtonClick(serviceName, 'EditKb', 'AddAlternate', 'Qnas', currentProjectId);
        setIsAddingNewQuestion(true);
    };

    return (
        <Stack>
            {isInEditMode ? (
                <Stack tokens={{ childrenGap: 8 }} styles={{ root: { marginBottom: `${mappedQuestions.length ? '8px' : '0'}` } }}>
                    {mappedQuestions.map(q => onRenderQuestionInEditMode(q))}
                </Stack>
            ) : (
                <DragAndDropList
                    items={mappedQuestions}
                    onRenderItem={onRenderQuestion}
                    canDragAndDrop={canWrite && !isInEditMode}
                    onEditItems={onDragAndDropItems}
                />
            )}
            {isAddingNewQuestion && (
                <EditModeDisplay
                    dir={languageDirection}
                    onSubmit={onAddQuestion}
                    onBlur={handleOnTakenTimeChange}
                    required={!mappedQuestions.length}
                    autoFocus={!mappedQuestions.length}
                    isComponentInEditMode={isInEditMode}
                    addAlternatePhraseButtonId={addAlternatePhraseButtonId.current}
                    onDismiss={isInAddMode && questions.length === 0 ? noop : onDismissForNewQuestion}
                />
            )}
            <RBACTooltip
                displayedUserRole={displayedUserRole}
                hidden={canWrite || isAddActionDisabled}
                data-automation-id="addAlternatePhraseButtonLanguageToolTip"
                componentSurroundedByToolTip={
                    <ActionButton
                        onClick={handleAddAlternatePhrase}
                        id={addAlternatePhraseButtonId.current}
                        disabled={!canWrite || isAddActionDisabled}
                        text={localize(messages.addAlternatePhrase)}
                        iconProps={{ iconName: 'Add', style: { fontSize: '14px' } }}
                    />
                }
            />
        </Stack>
    );
});
